import React, { useState, useEffect } from 'react';
import { getToken, fetchWithAuth, isSuperAdmin, getBranchId } from '../utils/auth';
import { API_URL } from '../config';
import './AdminQuestionGroups.css';

function AdminQuestionGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    questions: []
  });

  // Branch selector for super_admin
  const superAdmin = isSuperAdmin();
  const defaultBranchId = getBranchId() || 1;
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(defaultBranchId);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    loadQuestionGroups();
  }, [selectedBranchId]);

  const loadQuestionGroups = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/game/question-groups?branch_id=${selectedBranchId}`);
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      setError('Failed to load question groups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingGroup(null);
    setFormData({
      id: '',
      name: '',
      questions: []
    });
    setShowGroupForm(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group.id);
    setFormData({
      id: group.id,
      name: group.name,
      questions: [...group.questions]
    });
    setShowGroupForm(true);
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this question group?')) {
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        setError('You are not logged in. Please login again.');
        return;
      }

      const response = await fetch(`${API_URL}/api/game/question-groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete group');
      }

      await loadQuestionGroups();
    } catch (err) {
      setError('Failed to delete question group');
      console.error(err);
    }
  };

  const handleSaveGroup = async () => {
    if (!formData.id || !formData.name) {
      setError('Group ID and name are required');
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        setError('You are not logged in. Please login again.');
        return;
      }

      const url = editingGroup
        ? `${API_URL}/api/game/question-groups/${editingGroup}`
        : `${API_URL}/api/game/question-groups`;

      const method = editingGroup ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save group');
      }

      setShowGroupForm(false);
      await loadQuestionGroups();
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `q${formData.questions.length + 1}`,
      type: 'multiple_choice',
      text: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      timeLimit: 30
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
  };

  const handleUpdateQuestion = (index, field, value) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setFormData({
      ...formData,
      questions: updatedQuestions
    });
  };

  const handleUpdateOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    setFormData({
      ...formData,
      questions: updatedQuestions
    });
  };

  const handleDeleteQuestion = (index) => {
    const updatedQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      questions: updatedQuestions
    });
  };

  const handleQuestionTypeChange = (index, newType) => {
    const updatedQuestions = [...formData.questions];
    const question = updatedQuestions[index];

    if (newType === 'free_text') {
      delete question.options;
      delete question.correctAnswer;
    } else if (newType === 'multiple_choice') {
      question.options = ['', '', '', ''];
      question.correctAnswer = 0;
    }

    question.type = newType;
    setFormData({
      ...formData,
      questions: updatedQuestions
    });
  };

  if (loading) {
    return (
      <div className="admin-question-groups-container">
        <div className="loading">Loading question groups...</div>
      </div>
    );
  }

  if (showGroupForm) {
    return (
      <div className="admin-question-groups-container">
        <div className="form-header">
          <h1>{editingGroup ? 'Edit Question Group' : 'Create New Question Group'}</h1>
          <button className="close-btn" onClick={() => setShowGroupForm(false)}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="group-form">
          <div className="form-section">
            <h2>Group Details</h2>
            <div className="form-group">
              <label>Group ID (unique, lowercase-with-hyphens)</label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                disabled={editingGroup}
                placeholder="e.g., tamil-basics"
              />
            </div>

            <div className="form-group">
              <label>Group Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Tamil Basics - Sample Questions"
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h2>Questions ({formData.questions.length})</h2>
              <button className="add-question-btn" onClick={handleAddQuestion}>
                ➕ Add Question
              </button>
            </div>

            {formData.questions.map((question, qIndex) => (
              <div key={qIndex} className="question-card">
                <div className="question-header">
                  <span className="question-number">Question {qIndex + 1}</span>
                  <button
                    className="delete-question-btn"
                    onClick={() => handleDeleteQuestion(qIndex)}
                  >
                    🗑️ Delete
                  </button>
                </div>

                <div className="form-group">
                  <label>Question ID</label>
                  <input
                    type="text"
                    value={question.id}
                    onChange={(e) => handleUpdateQuestion(qIndex, 'id', e.target.value)}
                    placeholder="e.g., q1"
                  />
                </div>

                <div className="form-group">
                  <label>Question Type</label>
                  <select
                    value={question.type}
                    onChange={(e) => handleQuestionTypeChange(qIndex, e.target.value)}
                  >
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="free_text">Free Text (Word Cloud)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Question Text</label>
                  <textarea
                    value={question.text}
                    onChange={(e) => handleUpdateQuestion(qIndex, 'text', e.target.value)}
                    placeholder="Enter your question here"
                    rows="2"
                  />
                </div>

                {question.type === 'multiple_choice' && (
                  <>
                    <div className="form-group">
                      <label>Options</label>
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="option-input">
                          <span className="option-label">{String.fromCharCode(65 + oIndex)}.</span>
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => handleUpdateOption(qIndex, oIndex, e.target.value)}
                            placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                          />
                          {question.correctAnswer === oIndex && (
                            <span className="correct-indicator">✓ Correct</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="form-group">
                      <label>Correct Answer</label>
                      <select
                        value={question.correctAnswer}
                        onChange={(e) => handleUpdateQuestion(qIndex, 'correctAnswer', parseInt(e.target.value))}
                      >
                        {question.options.map((_, oIndex) => (
                          <option key={oIndex} value={oIndex}>
                            Option {String.fromCharCode(65 + oIndex)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Time Limit (seconds)</label>
                  <input
                    type="number"
                    value={question.timeLimit}
                    onChange={(e) => handleUpdateQuestion(qIndex, 'timeLimit', parseInt(e.target.value))}
                    min="10"
                    max="300"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button className="cancel-btn" onClick={() => setShowGroupForm(false)}>
              Cancel
            </button>
            <button className="save-btn" onClick={handleSaveGroup}>
              {editingGroup ? 'Update Group' : 'Create Group'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-question-groups-container">
      <div className="groups-header">
        <h1>Question Groups Management</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {superAdmin && branches.length > 0 && (
            <select
              value={selectedBranchId}
              onChange={e => setSelectedBranchId(Number(e.target.value))}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
            >
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button className="create-new-btn" onClick={handleCreateNew}>
            ➕ Create New Group
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {groups.length === 0 ? (
        <div className="no-groups">
          <div className="no-groups-icon">📝</div>
          <h2>No Question Groups</h2>
          <p>Create your first question group to get started!</p>
        </div>
      ) : (
        <div className="groups-grid">
          {groups.map((group) => (
            <div key={group.id} className="group-card">
              <div className="group-card-header">
                <h3>{group.name}</h3>
                <span className="group-id">{group.id}</span>
              </div>

              <div className="group-card-body">
                <div className="group-stat">
                  <span className="stat-icon">❓</span>
                  <span className="stat-value">{group.questions.length}</span>
                  <span className="stat-label">Questions</span>
                </div>

                <div className="question-types">
                  <div className="type-badge">
                    <span className="type-icon">☑️</span>
                    <span className="type-count">
                      {group.questions.filter(q => q.type === 'multiple_choice').length} Multiple Choice
                    </span>
                  </div>
                  <div className="type-badge">
                    <span className="type-icon">💭</span>
                    <span className="type-count">
                      {group.questions.filter(q => q.type === 'free_text').length} Free Text
                    </span>
                  </div>
                </div>
              </div>

              <div className="group-card-actions">
                <button
                  className="edit-btn"
                  onClick={() => handleEditGroup(group)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteGroup(group.id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminQuestionGroups;
